import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Instructor } from "@features/events/types";

interface UseInstructorsOptions {
  hccId?: string;
  instructorIds?: string[];
  enabled?: boolean;
}

type UseInstructorsResult = {
  instructors: Instructor[];
  allInstructors: Instructor[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch instructors for an event organizer.
 * Can filter by specific instructor IDs if provided.
 * Instructors are cached for 5 minutes.
 */
export const useInstructors = ({
  hccId,
  instructorIds,
  enabled = true,
}: UseInstructorsOptions): UseInstructorsResult => {
  const { GET } = useApiRequest();

  const { data: allInstructors = [], isLoading, error } = useQuery<Instructor[]>({
    queryKey: ["all-instructors", hccId],
    queryFn: () => GET(`/api/healthcare-companies/${hccId}/e/instructors`),
    enabled: Boolean(hccId && enabled),
    staleTime: 5 * 60 * 1000, // 5 minutes - instructors don't change often
  });

  // Filter instructors if IDs are provided
  const instructors = instructorIds
    ? allInstructors.filter((instructor) =>
        instructorIds.includes(instructor.id)
      )
    : allInstructors;

  return {
    instructors,
    allInstructors,
    isLoading,
    error: error ?? null,
  } satisfies UseInstructorsResult;
};

export default useInstructors;

